import { z } from 'zod';

import { REQUIRED } from '@/components/Form/validation';
import { countriesWithExtraInfo } from '@/lib/countries';

export const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'image/bmp'];
export const MAX_FILE_SIZE_MB = 10;

// Rendered by UploadImg's own formatMessage rather than through
// helperTextError, so these two need no `message.*` entry in the locale files.
export const INVALID_FILE_TYPE = 'invalid_file_type';
export const INVALID_FILE_SIZE = 'invalid_file_size';

const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const EXTRA_DATA_FIELDS = ['registration_number', 'vat_number'] as const;

export const createCompanySchema = z
  .object({
    name: z.string().trim().min(1, REQUIRED),
    // Picked from a fixed list, never typed, so there is nothing to trim.
    country: z.string().min(1, REQUIRED),
    registration_number: z.string().trim(),
    vat_number: z.string().trim(),
    picture: z
      .instanceof(File)
      .nullable()
      .refine((file) => !file || ACCEPTED_FILE_TYPES.includes(file.type), INVALID_FILE_TYPE)
      .refine((file) => !file || file.size <= MAX_FILE_SIZE_BYTES, INVALID_FILE_SIZE),
  })
  // Both numbers are required only for the countries that declare extra data,
  // so the rule reads a sibling field and cannot live on either one alone.
  .superRefine((values, ctx) => {
    if (!countriesWithExtraInfo.includes(values.country)) return;
    EXTRA_DATA_FIELDS.forEach((field) => {
      if (values[field]) return;
      ctx.addIssue({ code: 'custom', message: REQUIRED, path: [field] });
    });
  });

export type CreateCompanyValues = z.infer<typeof createCompanySchema>;
